import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import RMultilingual from '../Multilingual';
import Input from '~~elements/Input';
import Select from '~~elements/Select';
import Button from '~~elements/Button';
import Form from '~~elements/Form';
import { RegistrySectionDiv } from '../../styled';
import { useRegistryState } from '../useRegistry';
import { parseRequirementData } from '../service';
import { registrationColumnTypes, defaultTeamColumns, defaultStaffColumns, defaultPlayerColumns } from '../Const';

const DescriptionTextarea = styled.textarea`
  width: 100%;
  min-height: 200px;
  font-size: 0.9rem;
`;

const RegistrationColumnDiv = styled.div`
  width: 10rem;
  max-width: 80vw;
  text-align: left;
  background-color: white;
  border: 1px solid gray;
  border-radius: 5px;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  input:disabled {
    opacity: 0.8;
  }
`;

const StaffDiv = styled.div`
  width: 100%;
  border: 1px solid black;
  border-radius: 5px;
  padding: 1rem;
  margin: 0.5rem 0;
  position: relative;
  .staff-title {
    padding: 0 0 0.5rem 0;
    margin: 0 0.5rem 0.5rem;
    border-bottom: 1px solid gray;
  }
  .staff-remove {
    position: absolute;
    right: 0.3rem;
    top: 0.3rem;
  }
`;

const RequirementSection = ({ onChange, apiInProgress }) => {
  const { registryInfo } = useRegistryState();

  const [currentRequirementData, setCurrentRequirementData] = useState(parseRequirementData(registryInfo));

  useEffect(() => {
    setCurrentRequirementData(parseRequirementData(registryInfo));
  }, [registryInfo]);

  useEffect(() => {
    const originRequirementData = JSON.stringify(parseRequirementData(registryInfo));
    if (originRequirementData === JSON.stringify(currentRequirementData)) {
      onChange(null);
      return;
    }

    onChange(currentRequirementData);
    return;
  }, [currentRequirementData, onChange, registryInfo]);

  const handleInputOnChange = (e) => {
    setCurrentRequirementData({
      ...currentRequirementData,
      [e.target.name]: e.target.value,
    });
  };

  const handleAddTeamColumn = () => {
    setCurrentRequirementData({
      ...currentRequirementData,
      team_columns: [
        ...currentRequirementData.team_columns,
        {
          name: '',
          hint: '',
          type: 'TEXT',
          is_required: false,
        },
      ],
    });
  };

  const handleRemoveTeamColumn = (index) => (e) => {
    currentRequirementData.team_columns.splice(index, 1);
    setCurrentRequirementData({
      ...currentRequirementData,
      team_columns: [...currentRequirementData.team_columns],
    });
  };

  const handleTeamColumnOnChange = (index) => (e) => {
    let value = e.target.value;
    if (e.target.type === 'checkbox') {
      value = e.target.checked;
    }

    currentRequirementData.team_columns[index][e.target.name] = value;
    setCurrentRequirementData({
      ...currentRequirementData,
      team_columns: [...currentRequirementData.team_columns],
    });
  };

  const handleAddStaffTitle = () => {
    setCurrentRequirementData({
      ...currentRequirementData,
      staff_titles: [
        ...currentRequirementData.staff_titles,
        {
          title: '',
          is_required: false,
          columns: defaultStaffColumns.map((column) => ({
            name: RMultilingual(`DEFAULT_COLUMN.STAFF.${column.name}.NAME`),
            hint: RMultilingual(`DEFAULT_COLUMN.STAFF.${column.hint}.HINT`),
            type: column.type,
            is_required: column.is_required,
          })),
        },
      ],
    });
  };

  const handleRemoveStaffTitle = (tIndex) => (e) => {
    currentRequirementData.staff_titles.splice(tIndex, 1);
    setCurrentRequirementData({
      ...currentRequirementData,
      staff_titles: [...currentRequirementData.staff_titles],
    });
  };

  const handleStaffTitleOnChange = (tIndex) => (e) => {
    let value = e.target.value;
    if (e.target.type === 'checkbox') {
      value = e.target.checked;
    }

    currentRequirementData.staff_titles[tIndex][e.target.name] = value;
    setCurrentRequirementData({
      ...currentRequirementData,
      staff_titles: [...currentRequirementData.staff_titles],
    });
  };

  const handleAddStaffColumn = (tIndex) => () => {
    currentRequirementData.staff_titles[tIndex].columns = [
      ...currentRequirementData.staff_titles[tIndex].columns,
      {
        name: '',
        hint: '',
        type: 'TEXT',
        is_required: false,
      },
    ];

    setCurrentRequirementData({
      ...currentRequirementData,
      staff_titles: [...currentRequirementData.staff_titles],
    });
  };

  const handleRemoveStaffColumn = (tIndex, index) => () => {
    currentRequirementData.staff_titles[tIndex].columns.splice(index, 1);

    setCurrentRequirementData({
      ...currentRequirementData,
      staff_titles: [...currentRequirementData.staff_titles],
    });
  };

  const handleStaffColumnOnChange = (tIndex, index) => (e) => {
    let value = e.target.value;
    if (e.target.type === 'checkbox') {
      value = e.target.checked;
    }

    currentRequirementData.staff_titles[tIndex].columns[index][e.target.name] = value;
    setCurrentRequirementData({
      ...currentRequirementData,
      staff_titles: [...currentRequirementData.staff_titles],
    });
  };

  const handleAddPlayerColumn = () => {
    setCurrentRequirementData({
      ...currentRequirementData,
      player_columns: [
        ...currentRequirementData.player_columns,
        {
          name: '',
          hint: '',
          type: 'TEXT',
          is_required: false,
        },
      ],
    });
  };

  const handleRemovePlayerColumn = (index) => (e) => {
    currentRequirementData.player_columns.splice(index, 1);
    setCurrentRequirementData({
      ...currentRequirementData,
      player_columns: [...currentRequirementData.player_columns],
    });
  };

  const handlePlayerColumnOnChange = (index) => (e) => {
    let value = e.target.value;
    if (e.target.type === 'checkbox') {
      value = e.target.checked;
    }

    currentRequirementData.player_columns[index][e.target.name] = value;
    setCurrentRequirementData({
      ...currentRequirementData,
      player_columns: [...currentRequirementData.player_columns],
    });
  };

  return (
    <RegistrySectionDiv>
      <div className="section--description">{RMultilingual(`REQUIREMENT_DESCRIPTION`)}</div>
      <Form onSubmit={(e) => e.preventDefault()} className="lg">
        <div className="box">
          {/* 頭貼比例 */}
          <label>{RMultilingual('AVATAR_RATIO.WORDING')}</label>
          <div className="input-field --multi-input">
            <Input
              width="3rem"
              name="avatar_ratio_x"
              placeholder={RMultilingual(`AVATAR_RATIO.X`)}
              value={currentRequirementData.avatar_ratio_x}
              onChange={handleInputOnChange}
              disabled={apiInProgress}
            />
            &nbsp;:&nbsp;
            <Input
              width="3rem"
              name="avatar_ratio_y"
              placeholder={RMultilingual(`AVATAR_RATIO.Y`)}
              value={currentRequirementData.avatar_ratio_y}
              onChange={handleInputOnChange}
              disabled={apiInProgress}
            />
          </div>
          {/* 球隊資料蒐集 */}
          <label>{RMultilingual('TEAM_COLUMNS')}</label>
          <div className="input-field lg">
            <div className="input-list">
              {defaultTeamColumns.map((dc) => (
                <div key={dc.name}>
                  <RegistrationColumnDiv>
                    <label>{RMultilingual(`CUSTOM_COLUMN.NAME`)}</label>
                    <Input value={RMultilingual(`DEFAULT_COLUMN.TEAM.${dc.name}.NAME`)} disabled />
                    <label>
                      <input type="checkbox" checked={dc.is_required} disabled />
                      {RMultilingual(`CUSTOM_COLUMN.IS_REQUIRED`)}
                    </label>
                    <label>{RMultilingual(`CUSTOM_COLUMN.HINT`)}</label>
                    <Input
                      title={RMultilingual(`DEFAULT_COLUMN.TEAM.${dc.hint}.HINT`)}
                      value={RMultilingual(`DEFAULT_COLUMN.TEAM.${dc.hint}.HINT`)}
                      disabled
                    />
                    <label>{RMultilingual(`CUSTOM_COLUMN.TYPE.WORDING`)}</label>
                    <Select value={dc.type} disabled>
                      <option value={dc.type}>{RMultilingual(`CUSTOM_COLUMN.TYPE.${dc.type}`)}</option>
                    </Select>
                  </RegistrationColumnDiv>
                </div>
              ))}
              {currentRequirementData.team_columns.map((column, index) => (
                <div key={index} className="remove-icon-wrapper">
                  <RegistrationColumnDiv>
                    <label>
                      {RMultilingual(`CUSTOM_COLUMN.NAME`)}
                      <i>*</i>
                    </label>
                    <Input
                      name="name"
                      value={column.name}
                      disabled={apiInProgress}
                      onChange={handleTeamColumnOnChange(index)}
                    />
                    <label>
                      <input
                        name="is_required"
                        type="checkbox"
                        checked={column.is_required}
                        disabled={apiInProgress}
                        onChange={handleTeamColumnOnChange(index)}
                      />
                      {RMultilingual(`CUSTOM_COLUMN.IS_REQUIRED`)}
                    </label>
                    <label>{RMultilingual(`CUSTOM_COLUMN.HINT`)}</label>
                    <Input
                      name="hint"
                      title={column.hint}
                      value={column.hint}
                      disabled={apiInProgress}
                      onChange={handleTeamColumnOnChange(index)}
                    />
                    <label>{RMultilingual(`CUSTOM_COLUMN.TYPE.WORDING`)}</label>
                    <Select
                      name="type"
                      value={column.type}
                      disabled={apiInProgress}
                      onChange={handleTeamColumnOnChange(index)}
                    >
                      {registrationColumnTypes.map((rcType) => (
                        <option key={rcType} value={rcType}>
                          {RMultilingual(`CUSTOM_COLUMN.TYPE.${rcType}`)}
                        </option>
                      ))}
                    </Select>
                  </RegistrationColumnDiv>
                  <Button width="unset" color="transparent" mini onClick={handleRemoveTeamColumn(index)} type="button">
                    X
                  </Button>
                </div>
              ))}
              <label>
                <Button width="unset" color="transparentWithBorder" onClick={handleAddTeamColumn} type="button">
                  {RMultilingual(`COMMON.CREATE`)}
                </Button>
              </label>
            </div>
          </div>
          {/* 隨隊人員資料蒐集 */}
          <label>{RMultilingual('STAFF_TITLES')}</label>
          {currentRequirementData.staff_titles.map((title, tIndex) => (
            <StaffDiv key={tIndex}>
              <Button
                className="staff-remove"
                width="unset"
                color="transparent"
                mini
                onClick={handleRemoveStaffTitle(tIndex)}
                type="button"
              >
                X
              </Button>
              <div className="staff-title">
                <label>{RMultilingual(`DEFAULT_COLUMN.STAFF.TITLE.NAME`)}</label>
                <Input
                  name="title"
                  value={title.title}
                  disabled={apiInProgress}
                  placeholder={RMultilingual(`DEFAULT_COLUMN.STAFF.TITLE.HINT`)}
                  onChange={handleStaffTitleOnChange(tIndex)}
                />
                <label>
                  <input
                    name="is_required"
                    type="checkbox"
                    checked={title.is_required}
                    disabled={apiInProgress}
                    onChange={handleStaffTitleOnChange(tIndex)}
                  />
                  {RMultilingual(`CUSTOM_COLUMN.IS_REQUIRED`)}
                </label>
              </div>
              <div className="input-field lg">
                <div className="input-list">
                  {title.columns.map((column, index) => (
                    <div key={index} className="remove-icon-wrapper">
                      <RegistrationColumnDiv>
                        <label>
                          {RMultilingual(`CUSTOM_COLUMN.NAME`)}
                          <i>*</i>
                        </label>
                        <Input
                          name="name"
                          value={column.name}
                          disabled={apiInProgress}
                          onChange={handleStaffColumnOnChange(tIndex, index)}
                        />
                        <label>
                          <input
                            name="is_required"
                            type="checkbox"
                            checked={column.is_required}
                            disabled={apiInProgress}
                            onChange={handleStaffColumnOnChange(tIndex, index)}
                          />
                          {RMultilingual(`CUSTOM_COLUMN.IS_REQUIRED`)}
                        </label>
                        <label>{RMultilingual(`CUSTOM_COLUMN.HINT`)}</label>
                        <Input
                          name="hint"
                          title={column.hint}
                          value={column.hint}
                          disabled={apiInProgress}
                          onChange={handleStaffColumnOnChange(tIndex, index)}
                        />
                        <label>{RMultilingual(`CUSTOM_COLUMN.TYPE.WORDING`)}</label>
                        <Select
                          name="type"
                          value={column.type}
                          disabled={apiInProgress}
                          onChange={handleStaffColumnOnChange(tIndex, index)}
                        >
                          {registrationColumnTypes.map((rcType) => (
                            <option key={rcType} value={rcType}>
                              {RMultilingual(`CUSTOM_COLUMN.TYPE.${rcType}`)}
                            </option>
                          ))}
                        </Select>
                      </RegistrationColumnDiv>
                      <Button
                        width="unset"
                        color="transparent"
                        mini
                        onClick={handleRemoveStaffColumn(tIndex, index)}
                        type="button"
                      >
                        X
                      </Button>
                    </div>
                  ))}
                  <label>
                    <Button
                      width="unset"
                      color="transparentWithBorder"
                      onClick={handleAddStaffColumn(tIndex)}
                      type="button"
                    >
                      {RMultilingual(`COMMON.CREATE`)}
                    </Button>
                  </label>
                </div>
              </div>
            </StaffDiv>
          ))}
          <Button width="unset" color="transparentWithBorder" onClick={handleAddStaffTitle} type="button">
            {`${RMultilingual(`COMMON.CREATE`)} ${RMultilingual(`STAFF`)}`}
          </Button>
          <div className="input-field" />
          {/* 每隊球員上限 */}
          <label>{RMultilingual('PLAYER_LIMIT')}</label>
          <div className="input-field">
            <Input
              width="10rem"
              name="player_limit"
              placeholder={RMultilingual(`PLAYER_LIMIT`)}
              value={currentRequirementData.player_limit}
              onChange={handleInputOnChange}
              disabled={apiInProgress}
            />
          </div>
          {/* 報名球員需求 */}
          <label>{RMultilingual('PLAYER_DESCRIPTION')}</label>
          <div className="input-field">
            <DescriptionTextarea
              name="player_description"
              value={currentRequirementData.player_description}
              onChange={handleInputOnChange}
              disabled={apiInProgress}
            />
          </div>
          {/* 球員資料蒐集 */}
          <label>{RMultilingual('PLAYER_COLUMNS')}</label>
          <div className="input-field lg">
            <div className="input-list">
              {defaultPlayerColumns.map((dc) => (
                <div key={dc.name}>
                  <RegistrationColumnDiv>
                    <label>{RMultilingual(`CUSTOM_COLUMN.NAME`)}</label>
                    <Input value={RMultilingual(`DEFAULT_COLUMN.PLAYER.${dc.name}.NAME`)} disabled />
                    <label>
                      <input type="checkbox" checked={dc.is_required} disabled />
                      {RMultilingual(`CUSTOM_COLUMN.IS_REQUIRED`)}
                    </label>
                    <label>{RMultilingual(`CUSTOM_COLUMN.HINT`)}</label>
                    <Input
                      title={RMultilingual(`DEFAULT_COLUMN.PLAYER.${dc.hint}.HINT`)}
                      value={RMultilingual(`DEFAULT_COLUMN.PLAYER.${dc.hint}.HINT`)}
                      disabled
                    />
                    <label>{RMultilingual(`CUSTOM_COLUMN.TYPE.WORDING`)}</label>
                    <Select value={dc.type} disabled>
                      <option value={dc.type}>{RMultilingual(`CUSTOM_COLUMN.TYPE.${dc.type}`)}</option>
                    </Select>
                  </RegistrationColumnDiv>
                </div>
              ))}
              {currentRequirementData.player_columns.map((column, index) => (
                <div key={index} className="remove-icon-wrapper">
                  <RegistrationColumnDiv>
                    <label>
                      {RMultilingual(`CUSTOM_COLUMN.NAME`)}
                      <i>*</i>
                    </label>
                    <Input
                      name="name"
                      value={column.name}
                      disabled={apiInProgress}
                      onChange={handlePlayerColumnOnChange(index)}
                    />
                    <label>
                      <input
                        name="is_required"
                        type="checkbox"
                        checked={column.is_required}
                        disabled={apiInProgress}
                        onChange={handlePlayerColumnOnChange(index)}
                      />
                      {RMultilingual(`CUSTOM_COLUMN.IS_REQUIRED`)}
                    </label>
                    <label>{RMultilingual(`CUSTOM_COLUMN.HINT`)}</label>
                    <Input
                      name="hint"
                      title={column.hint}
                      value={column.hint}
                      disabled={apiInProgress}
                      onChange={handlePlayerColumnOnChange(index)}
                    />
                    <label>{RMultilingual(`CUSTOM_COLUMN.TYPE.WORDING`)}</label>
                    <Select
                      name="type"
                      value={column.type}
                      disabled={apiInProgress}
                      onChange={handlePlayerColumnOnChange(index)}
                    >
                      {registrationColumnTypes.map((rcType) => (
                        <option key={rcType} value={rcType}>
                          {RMultilingual(`CUSTOM_COLUMN.TYPE.${rcType}`)}
                        </option>
                      ))}
                    </Select>
                  </RegistrationColumnDiv>
                  <Button
                    width="unset"
                    color="transparent"
                    mini
                    onClick={handleRemovePlayerColumn(index)}
                    type="button"
                  >
                    X
                  </Button>
                </div>
              ))}
              <label>
                <Button width="unset" color="transparentWithBorder" onClick={handleAddPlayerColumn} type="button">
                  {RMultilingual(`COMMON.CREATE`)}
                </Button>
              </label>
            </div>
          </div>
        </div>
      </Form>
    </RegistrySectionDiv>
  );
};

export default RequirementSection;
